// ============================================
// CLIENTESNUEVO.JS — FASE 1
// Panel unificado: grupos, empresas, personas, sucursales
// Alta rápida con datos mínimos. Fichas detalladas por tipo.
// ============================================

import React, { useEffect, useMemo, useState } from 'react';
import FichaGrupo from './components/FichaGrupo';
import FichaRazonSocial from './components/FichaRazonSocial';
import FichaSucursal from './components/FichaSucursal';
import FichaPersona from './components/FichaPersona';
import DetectorDuplicados from './components/DetectorDuplicados';
import ImportadorCSV from './components/ImportadorCSV';
import BotonArchivarCliente from './components/BotonArchivarCliente';
import AccionesArchivado from './components/AccionesArchivado';
import ModalConvertirSucursal from './components/ModalConvertirSucursal';
import ModalBorrarArchivados from './components/ModalBorrarArchivados';
import { useUsuario } from './context/UsuarioContext';
import { authHeaders } from './utils/storage';
import { API_BASE } from './config/api';

const PP = "'Poppins', sans-serif";
const COLOR_PRINCIPAL = '#5c9adb';
const COLOR_OSCURO = '#2f5f9e';
const COLOR_FONDO = '#f3f4f4';
const COLOR_GRIS = '#4e5357';
const COLOR_GRIS_CLARO = '#8a8f95';
const COLOR_BORDE = '#e6e8ea';

// ESTADOS y ESTADO_VISUAL se consolidaron en un solo objeto al final del archivo.
// El "punto naranja" venía de aquí: ESTADOS.operativo.color = '#f4a261' (naranja)
// pero ESTADO_VISUAL.operativo.color = '#10b981' (verde). Dot y badge se
// contradecían. Ahora ambos leen de ESTADO_VISUAL.

const FILTROS = [
  { id: 'todos',      label: 'Todos' },
  { id: 'grupos',     label: 'Grupos (clientes)' },
  // TAREA A (2026-07-04): etiqueta visible renombrada a "Razones Sociales";
  // el id interno 'empresas' NO cambia (localStorage de filtros y logica intactos).
  { id: 'empresas',   label: 'Razones Sociales' },
  { id: 'personas',   label: 'Personas' },
  { id: 'sucursales', label: 'Sucursales' },
  { id: 'archivados', label: '📦 Archivados' },
];

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

// Llave de localStorage donde persistimos pestaña + filtros activos.
// El reset al cambiar de usuario se maneja via comparación de token.
const STORAGE_KEY_FILTROS = 'beep_clientes_filtros';
const STORAGE_KEY_TOKEN_PREVIO = 'beep_clientes_token_previo';

function leerFiltrosGuardados() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_FILTROS);
    if (!raw) return null;
    const o = JSON.parse(raw);
    return (o && typeof o === 'object') ? o : null;
  } catch { return null; }
}

export default function ClientesNuevo({ esCelular }) {
  // Hidrata pestaña + filtros desde localStorage, con reset si el token cambió.
  const filtrosGuardados = useMemo(() => {
    try {
      const tokenActual = localStorage.getItem('beep_token') || '';
      const tokenPrevio = localStorage.getItem(STORAGE_KEY_TOKEN_PREVIO) || '';
      if (tokenActual && tokenActual !== tokenPrevio) {
        // Cambio de usuario / sesión nueva → limpia filtros del usuario anterior
        localStorage.removeItem(STORAGE_KEY_FILTROS);
        localStorage.setItem(STORAGE_KEY_TOKEN_PREVIO, tokenActual);
        return null;
      }
      return leerFiltrosGuardados();
    } catch { return null; }
  }, []);

  const [filtro, setFiltro]         = useState(filtrosGuardados?.pestana || 'todos');
  const [estadoFiltro, setEstadoFiltro] = useState(filtrosGuardados?.estadoFiltro || null);
  const [grupoFiltro, setGrupoFiltro]   = useState(filtrosGuardados?.grupoFiltro || null);
  const [ciudadFiltro, setCiudadFiltro] = useState(filtrosGuardados?.ciudadFiltro || null);
  const [busqueda, setBusqueda] = useState('');
  const [grupos, setGrupos] = useState([]);
  const [razones, setRazones] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [seleccion, setSeleccion] = useState(null); // { tipo, id }
  const [modalCrear, setModalCrear] = useState(false);
  const [modalImportar, setModalImportar] = useState(false);
  // SPRINT SUCURSALES 1: seleccion multiple de razones sociales para
  // "Convertir en sucursal de..." (pestana Razones Sociales).
  // BORRADO MASIVO: seleccion multiple de los 4 tipos en la pestana Archivados.
  const [modoSeleccion, setModoSeleccion] = useState(false);
  const [empresasSeleccionadas, setEmpresasSeleccionadas] = useState(() => new Set());
  const [archivadosSeleccionados, setArchivadosSeleccionados] = useState(() => new Set()); // keys `${tipo}-${id}`
  const [modalConvertir, setModalConvertir] = useState(null); // null | [{id, nombre, grupoId, grupoNombre}]
  const [modalBorrar, setModalBorrar] = useState(null); // null | [{tipo, id, nombre}]
  // Solo Direccion puede borrar permanente (mismo gating que AccionesArchivado).
  const { accesoTotal } = useUsuario();

  // Al cambiar de pestana se apaga el modo seleccion y se limpian ambas selecciones.
  useEffect(() => {
    setModoSeleccion(false);
    setEmpresasSeleccionadas(new Set());
    setArchivadosSeleccionados(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtro]);

  const toggleEmpresaSeleccionada = (id) => {
    setEmpresasSeleccionadas(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const toggleArchivadoSeleccionado = (key) => {
    setArchivadosSeleccionados(prev => {
      const s = new Set(prev);
      if (s.has(key)) s.delete(key); else s.add(key);
      return s;
    });
  };

  const abrirModalConvertir = (idsSet) => {
    const lista = razones
      .filter(r => idsSet.has(r.id))
      .map(r => ({ id: r.id, nombre: r.razon_social, grupoId: r.grupo_id, grupoNombre: r.grupo_nombre }));
    if (lista.length > 0) setModalConvertir(lista);
  };

  // Persistir filtros al cambiar
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_FILTROS, JSON.stringify({
        pestana: filtro,
        estadoFiltro,
        grupoFiltro,
        ciudadFiltro,
      }));
    } catch {}
  }, [filtro, estadoFiltro, grupoFiltro, ciudadFiltro]);

  // El estado a pedir al backend depende del filtro activo:
  //   pestaña 'archivados' → ?estado=archivados
  //   resto                → ?estado=activos (default del backend)
  const estadoQuery = filtro === 'archivados' ? 'archivados' : 'activos';
  const recargar = async () => {
    setCargando(true);
    try {
      const qs = `?estado=${estadoQuery}`;
      const [g, r, s, p] = await Promise.all([
        fetch(`${API_BASE}/api/clientes/grupos${qs}`, { headers: authHeaders() }).then(x => x.json()),
        fetch(`${API_BASE}/api/clientes/razones-sociales${qs}`, { headers: authHeaders() }).then(x => x.json()),
        fetch(`${API_BASE}/api/clientes/sucursales${qs}`, { headers: authHeaders() }).then(x => x.json()),
        fetch(`${API_BASE}/api/clientes/personas${qs}`, { headers: authHeaders() }).then(x => x.json()),
      ]);
      setGrupos(g.grupos || []);
      setRazones(r.razones_sociales || []);
      setSucursales(s.sucursales || []);
      setPersonas(p.personas || []);
    } catch (e) {
      console.error('Error cargando clientes:', e);
    } finally {
      setCargando(false);
    }
  };

  // Contador de archivados (independiente de la pestaña activa) para mostrar
  // en el botón "📦 Archivados (N)". Se refresca cuando recargar() corre.
  const [conteoArchivados, setConteoArchivados] = useState(0);
  useEffect(() => {
    let activo = true;
    Promise.all([
      fetch(`${API_BASE}/api/clientes/grupos?estado=archivados`, { headers: authHeaders() }).then(x => x.json()).catch(() => ({})),
      fetch(`${API_BASE}/api/clientes/razones-sociales?estado=archivados`, { headers: authHeaders() }).then(x => x.json()).catch(() => ({})),
      fetch(`${API_BASE}/api/clientes/sucursales?estado=archivados`, { headers: authHeaders() }).then(x => x.json()).catch(() => ({})),
      fetch(`${API_BASE}/api/clientes/personas?estado=archivados`, { headers: authHeaders() }).then(x => x.json()).catch(() => ({})),
    ]).then(([g, r, s, p]) => {
      if (!activo) return;
      const n = (g.grupos?.length || 0) + (r.razones_sociales?.length || 0)
              + (s.sucursales?.length || 0) + (p.personas?.length || 0);
      setConteoArchivados(n);
    });
    return () => { activo = false; };
  }, [filtro]); // se refresca al cambiar de pestaña (incluye después de archivar/restaurar)

  // Refresca al montar y al cambiar de pestaña (cambia el ?estado= que se envía).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { recargar(); }, [estadoQuery]);

  // Mapa nombre→id de grupos para resolver grupo de personas (que solo
  // expone rol_principal_grupo_nombre, no _id).
  const grupoNombreToId = useMemo(() => {
    const m = new Map();
    for (const g of grupos) if (g?.nombre) m.set(g.nombre, g.id);
    return m;
  }, [grupos]);

  // Lista mezclada, filtrada y buscada. Devuelve { items, totalSinFiltros }
  // para que el contador pueda mostrar "X de Y mostrados" cuando hay
  // filtros avanzados activos.
  const { items, totalSinFiltros } = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    const matchTexto = (s) => !q || (s || '').toLowerCase().includes(q);
    const mix = [];
    const esArchivados = filtro === 'archivados';
    // En 'todos' y 'archivados' mostramos los 4 tipos. En el resto sólo el correspondiente.
    const mostrarGrupos     = filtro === 'todos' || filtro === 'grupos'     || esArchivados;
    const mostrarEmpresas   = filtro === 'todos' || filtro === 'empresas'   || esArchivados;
    const mostrarPersonas   = filtro === 'todos' || filtro === 'personas'   || esArchivados;
    const mostrarSucursales = filtro === 'sucursales' || esArchivados;

    if (mostrarGrupos) {
      grupos.forEach(g => {
        if (matchTexto(g.nombre)) {
          mix.push({
            tipo: 'grupo', id: g.id, nombre: g.nombre,
            estado: g.estado, icono: '📁',
            resumen: `${g.razones_count || 0} razon${g.razones_count === 1 ? '' : 'es'} sociales · ${g.sucursales_count || 0} sucursal${g.sucursales_count === 1 ? '' : 'es'}`,
            _grupoId: g.id, // un grupo es su propio "grupo"
            _ciudad: null,
          });
        }
      });
    }
    if (mostrarEmpresas) {
      razones.forEach(rs => {
        if (filtro === 'todos' && rs.grupo_id) return; // en 'todos' las que tienen grupo se ven dentro del grupo
        if (matchTexto(rs.razon_social) || matchTexto(rs.rfc)) {
          // Jerarquía: "Grupo · RFC: X · N sucursales"
          const partes = [];
          if (rs.grupo_nombre) partes.push(rs.grupo_nombre);
          if (rs.rfc) partes.push(`RFC: ${rs.rfc}`);
          partes.push(`${rs.sucursales_count || 0} sucursal${rs.sucursales_count === 1 ? '' : 'es'}`);
          mix.push({
            tipo: 'razon_social', id: rs.id, nombre: rs.razon_social,
            estado: rs.estado, icono: '🏢',
            resumen: partes.join(' · '),
            _grupoId: rs.grupo_id || null,
            _ciudad: null,
          });
        }
      });
    }
    if (mostrarPersonas) {
      personas.forEach(p => {
        if (matchTexto(p.nombre_completo) || matchTexto(p.email) || matchTexto(p.celular)) {
          // Jerarquía del rol principal: "Grupo › Razón › Sucursal · Rol · Tel"
          const jerarquia = [
            p.rol_principal_grupo_nombre,
            p.rol_principal_razon_social_nombre,
            p.rol_principal_alcance_tipo === 'sucursal' ? p.rol_principal_alcance_nombre : null,
          ].filter(Boolean).join(' › ');
          const tail = [];
          if (p.rol_principal_tipo) tail.push(etiquetaRol(p.rol_principal_tipo));
          if (p.celular || p.email) tail.push(p.celular || p.email);
          if (p.es_cliente_persona_fisica) tail.unshift('Cliente persona física');
          let resumen;
          if (jerarquia) {
            resumen = tail.length ? `${jerarquia} · ${tail.join(' · ')}` : jerarquia;
          } else if (p.rol_principal_alcance_nombre) {
            resumen = [p.rol_principal_alcance_nombre, ...tail].filter(Boolean).join(' · ');
          } else {
            resumen = [
              p.es_cliente_persona_fisica ? 'Cliente persona física' : null,
              p.roles_count > 0 ? `${p.roles_count} rol${p.roles_count === 1 ? '' : 'es'}` : 'Sin razón social',
              p.celular || p.email || null,
            ].filter(Boolean).join(' · ');
          }
          // Resuelve grupo de la persona vía nombre del grupo del rol principal.
          // Si dos grupos tienen el mismo nombre, gana el primero (edge case raro).
          const grupoIdPersona = p.rol_principal_grupo_nombre
            ? (grupoNombreToId.get(p.rol_principal_grupo_nombre) || null)
            : null;
          mix.push({
            tipo: 'persona', id: p.id, nombre: p.nombre_completo,
            estado: p.estado, icono: p.es_cliente_persona_fisica ? '👤' : '👥',
            resumen,
            _grupoId: grupoIdPersona,
            _ciudad: null,
          });
        }
      });
    }
    if (mostrarSucursales) {
      sucursales.forEach(s => {
        if (matchTexto(s.nombre_interno) || matchTexto(s.direccion) || matchTexto(s.razon_social_nombre)) {
          const jerarquia = [s.grupo_nombre, s.razon_social_nombre].filter(Boolean).join(' › ');
          const detalles = [s.ciudad, s.direccion].filter(Boolean).join(' · ');
          const resumen = [jerarquia, detalles].filter(Boolean).join(' · ');
          mix.push({
            tipo: 'sucursal', id: s.id, nombre: s.nombre_interno,
            estado: s.estado, icono: '🏪',
            resumen,
            _grupoId: s.grupo_id_efectivo || s.grupo_id || null,
            _ciudad: s.ciudad || null,
          });
        }
      });
    }

    // Capa de filtros avanzados (estado / grupo / ciudad). En la pestaña
    // Archivados estos filtros NO aplican (todos son archivados).
    const aplicarFiltrosAvanzados = !esArchivados;
    const filtrado = !aplicarFiltrosAvanzados ? mix : mix.filter(it => {
      if (estadoFiltro && it.estado !== estadoFiltro) return false;
      if (grupoFiltro != null && it._grupoId !== grupoFiltro) return false;
      if (ciudadFiltro) {
        if (it.tipo !== 'sucursal') return false;
        if (it._ciudad !== ciudadFiltro) return false;
      }
      return true;
    });

    // Orden: básicos primero (para recordar lo pendiente), luego alfabético
    filtrado.sort((a, b) => {
      const ord = { basico: 0, operativo: 1, listo_facturar: 2, archivado: 3 };
      const oa = ord[a.estado] ?? 9, ob = ord[b.estado] ?? 9;
      if (oa !== ob) return oa - ob;
      return (a.nombre || '').localeCompare(b.nombre || '');
    });
    return { items: filtrado, totalSinFiltros: mix.length };
  }, [filtro, busqueda, grupos, razones, sucursales, personas,
      estadoFiltro, grupoFiltro, ciudadFiltro, grupoNombreToId]);

  const total = items.length;
  // Hay filtros avanzados activos? (estado, grupo, ciudad)
  const hayFiltrosAvanzados = !!(estadoFiltro || grupoFiltro || ciudadFiltro);
  // Opciones para los dropdowns: derivadas de las listas cargadas
  const grupoOpciones = useMemo(
    () => grupos.slice().sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '')),
    [grupos]
  );
  const ciudadOpciones = useMemo(() => {
    const set = new Set();
    for (const s of sucursales) if (s.ciudad) set.add(s.ciudad);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [sucursales]);
  const limpiarFiltros = () => {
    setEstadoFiltro(null);
    setGrupoFiltro(null);
    setCiudadFiltro(null);
  };

  // Si hay selección, mostrar ficha a pantalla completa
  if (seleccion) {
    const volver = () => { setSeleccion(null); recargar(); };
    if (seleccion.tipo === 'grupo')         return <FichaGrupo id={seleccion.id} onVolver={volver} esCelular={esCelular} />;
    if (seleccion.tipo === 'razon_social')  return <FichaRazonSocial id={seleccion.id} onVolver={volver} esCelular={esCelular} />;
    if (seleccion.tipo === 'sucursal')      return <FichaSucursal id={seleccion.id} onVolver={volver} esCelular={esCelular} />;
    if (seleccion.tipo === 'persona')       return <FichaPersona id={seleccion.id} onVolver={volver} esCelular={esCelular} />;
  }

  return (
    <div style={{ fontFamily: PP, background: COLOR_FONDO, minHeight: '100%', color: COLOR_GRIS }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: esCelular ? '16px' : '20px 28px', borderBottom: `1px solid ${COLOR_BORDE}`,
        background: '#fff', flexWrap: 'wrap', gap: 12,
      }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: COLOR_OSCURO }}>Clientes</div>
          <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 2 }}>
            {cargando
              ? 'Cargando…'
              : (hayFiltrosAvanzados
                  ? `${total} de ${totalSinFiltros} mostrado${total === 1 ? '' : 's'}`
                  : `${total} resultado${total === 1 ? '' : 's'}`)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {(filtro === 'empresas' || (filtro === 'archivados' && accesoTotal)) && (
            <button
              onClick={() => {
                setModoSeleccion(v => !v);
                setEmpresasSeleccionadas(new Set());
                setArchivadosSeleccionados(new Set());
              }}
              onPointerDown={(e) => { e.currentTarget.style.opacity = '0.6'; }}
              onPointerUp={(e) => { e.currentTarget.style.opacity = '1'; }}
              onPointerLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
              style={{
                background: modoSeleccion ? COLOR_OSCURO : '#fff',
                color: modoSeleccion ? '#fff' : COLOR_OSCURO,
                border: `1px solid ${modoSeleccion ? COLOR_OSCURO : COLOR_BORDE}`,
                padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                cursor: 'pointer', fontFamily: PP, minHeight: 44, transition: 'opacity 0.1s',
              }}
            >
              {modoSeleccion ? '✕ Cancelar seleccion' : '☑️ Seleccionar'}
            </button>
          )}
          {filtro === 'archivados' && modoSeleccion && (
            <button
              onClick={() => {
                // Selecciona/deselecciona todo lo VISIBLE con el filtro/busqueda actual.
                const keys = items.map(it => `${it.tipo}-${it.id}`);
                const todosMarcados = keys.length > 0 && keys.every(k => archivadosSeleccionados.has(k));
                setArchivadosSeleccionados(todosMarcados ? new Set() : new Set(keys));
              }}
              onPointerDown={(e) => { e.currentTarget.style.opacity = '0.6'; }}
              onPointerUp={(e) => { e.currentTarget.style.opacity = '1'; }}
              onPointerLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
              style={{
                background: '#fff', color: COLOR_OSCURO, border: `1px solid ${COLOR_BORDE}`,
                padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                cursor: 'pointer', fontFamily: PP, minHeight: 44, transition: 'opacity 0.1s',
              }}
            >
              {items.length > 0 && items.every(it => archivadosSeleccionados.has(`${it.tipo}-${it.id}`))
                ? '⬜ Quitar todos'
                : `☑️ Seleccionar todos (${items.length})`}
            </button>
          )}
          {filtro === 'archivados' && modoSeleccion && archivadosSeleccionados.size > 0 && (
            <button
              onClick={() => {
                const registros = items
                  .filter(it => archivadosSeleccionados.has(`${it.tipo}-${it.id}`))
                  .map(it => ({ tipo: it.tipo, id: it.id, nombre: it.nombre }));
                if (registros.length > 0) setModalBorrar(registros);
              }}
              onPointerDown={(e) => { e.currentTarget.style.opacity = '0.6'; }}
              onPointerUp={(e) => { e.currentTarget.style.opacity = '1'; }}
              onPointerLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
              style={{
                background: '#e05c5c', color: '#fff', border: 'none',
                padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 700,
                cursor: 'pointer', fontFamily: PP, minHeight: 44, transition: 'opacity 0.1s',
              }}
            >
              🗑 Borrar seleccionados ({archivadosSeleccionados.size})
            </button>
          )}
          {filtro === 'empresas' && modoSeleccion && empresasSeleccionadas.size > 0 && (
            <button
              onClick={() => abrirModalConvertir(empresasSeleccionadas)}
              onPointerDown={(e) => { e.currentTarget.style.opacity = '0.6'; }}
              onPointerUp={(e) => { e.currentTarget.style.opacity = '1'; }}
              onPointerLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
              style={{
                background: COLOR_PRINCIPAL, color: '#fff', border: 'none',
                padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 700,
                cursor: 'pointer', fontFamily: PP, minHeight: 44, transition: 'opacity 0.1s',
              }}
            >
              🏬 Convertir en sucursal de… ({empresasSeleccionadas.size})
            </button>
          )}
          <button
            onClick={() => setModalImportar(true)}
            style={{
              background: '#fff', color: COLOR_OSCURO, border: `1px solid ${COLOR_BORDE}`,
              padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
              cursor: 'pointer', fontFamily: PP,
            }}
          >
            📥 Importar CSV
          </button>
          <button
            onClick={() => setModalCrear(true)}
            style={{
              background: COLOR_PRINCIPAL, color: '#fff', border: 'none',
              padding: '10px 18px', borderRadius: 10, fontSize: 13, fontWeight: 600,
              cursor: 'pointer', fontFamily: PP,
            }}
          >
            + Nuevo cliente
          </button>
        </div>
      </div>

      {/* Búsqueda + filtros */}
      <div style={{
        display: 'flex', flexDirection: 'column', gap: 10,
        padding: esCelular ? '12px 16px' : '16px 28px',
        borderBottom: `1px solid ${COLOR_BORDE}`, background: '#fff',
      }}>
        <input
          type="text"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar por nombre, razón social, RFC, email, celular…"
          style={{
            width: '100%', padding: '10px 14px', border: `1px solid ${COLOR_BORDE}`,
            borderRadius: 10, fontSize: 13, fontFamily: PP, outline: 'none',
            background: COLOR_FONDO,
          }}
        />
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {FILTROS.map(f => {
            const activo = filtro === f.id;
            const labelConContador = f.id === 'archivados'
              ? `${f.label} (${conteoArchivados})`
              : f.label;
            return (
              <button
                key={f.id}
                onClick={() => setFiltro(f.id)}
                style={{
                  padding: '6px 14px', borderRadius: 99, border: 'none',
                  background: activo ? COLOR_PRINCIPAL : COLOR_FONDO,
                  color: activo ? '#fff' : COLOR_GRIS,
                  fontSize: 12, fontWeight: activo ? 600 : 500,
                  cursor: 'pointer', fontFamily: PP,
                }}
              >
                {labelConContador}
              </button>
            );
          })}
        </div>

        {/* CAPA 1 — Chips de estado (oculto en pestaña Archivados) */}
        {filtro !== 'archivados' && (
          <ChipsEstado valor={estadoFiltro} onCambio={setEstadoFiltro} />
        )}

        {/* CAPA 2 — Dropdowns avanzados (Grupo / Ciudad) */}
        {filtro !== 'archivados' && (grupoOpciones.length >= 2 || ciudadOpciones.length >= 2) && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {grupoOpciones.length >= 2 && filtro !== 'grupos' && (
              <DropdownFiltro
                label="Grupo (cliente)" todosLabel="Todos los grupos (clientes)"
                valor={grupoFiltro}
                opciones={grupoOpciones.map(g => ({ value: g.id, label: g.nombre }))}
                onCambio={setGrupoFiltro}
              />
            )}
            {ciudadOpciones.length >= 2 && filtro !== 'grupos' && (
              <DropdownFiltro
                label="Ciudad" todosLabel="Todas las ciudades"
                valor={ciudadFiltro}
                opciones={ciudadOpciones.map(c => ({ value: c, label: c }))}
                onCambio={setCiudadFiltro}
              />
            )}
          </div>
        )}

        {/* CAPA 3 — Resumen + limpiar (solo si hay filtros avanzados activos) */}
        {filtro !== 'archivados' && hayFiltrosAvanzados && (
          <ResumenFiltros
            estadoFiltro={estadoFiltro}
            grupoFiltro={grupoFiltro}
            ciudadFiltro={ciudadFiltro}
            grupoOpciones={grupoOpciones}
            onQuitarEstado={() => setEstadoFiltro(null)}
            onQuitarGrupo={() => setGrupoFiltro(null)}
            onQuitarCiudad={() => setCiudadFiltro(null)}
            onLimpiar={limpiarFiltros}
          />
        )}
      </div>

      {/* Lista */}
      <div style={{ padding: esCelular ? '12px' : '20px 28px' }}>
        {cargando && <div style={{ textAlign: 'center', padding: 40, color: COLOR_GRIS_CLARO }}>Cargando clientes…</div>}
        {!cargando && items.length === 0 && (
          <div style={{ textAlign: 'center', padding: 60, color: COLOR_GRIS_CLARO, fontSize: 13 }}>
            {busqueda
              ? 'Sin resultados para la búsqueda actual.'
              : 'Aún no hay clientes. Crea el primero con "+ Nuevo cliente".'}
          </div>
        )}
        <div style={{ display: 'grid', gap: 8 }}>
          {items.map(it => {
            const selEmpresas = modoSeleccion && filtro === 'empresas' && it.tipo === 'razon_social';
            // BORRADO MASIVO: en Archivados se seleccionan los 4 tipos.
            const selArchivados = modoSeleccion && filtro === 'archivados';
            const seleccionable = selEmpresas || selArchivados;
            const key = `${it.tipo}-${it.id}`;
            return (
              <ItemLista
                key={key}
                item={it}
                onClick={selEmpresas
                  ? () => toggleEmpresaSeleccionada(it.id)
                  : (selArchivados
                    ? () => toggleArchivadoSeleccionado(key)
                    : () => setSeleccion({ tipo: it.tipo, id: it.id }))}
                onArchivado={recargar}
                mostrarArchivados={filtro === 'archivados'}
                seleccionable={seleccionable}
                seleccionado={selEmpresas
                  ? empresasSeleccionadas.has(it.id)
                  : (selArchivados && archivadosSeleccionados.has(key))}
                ocultarAcciones={selArchivados}
              />
            );
          })}
        </div>
      </div>

      {modalCrear && (
        <ModalCrear
          onCerrar={() => setModalCrear(false)}
          onCreado={(sel) => { setModalCrear(false); recargar(); if (sel) setSeleccion(sel); }}
          razones={razones}
          grupos={grupos}
        />
      )}
      {modalImportar && (
        <ImportadorCSV
          onCerrar={() => setModalImportar(false)}
          onCompletado={() => { setModalImportar(false); recargar(); }}
        />
      )}
      {modalConvertir && (
        <ModalConvertirSucursal
          empresas={modalConvertir}
          onCerrar={() => setModalConvertir(null)}
          onConvertidas={() => {
            setModalConvertir(null);
            setModoSeleccion(false);
            setEmpresasSeleccionadas(new Set());
            recargar();
          }}
        />
      )}
      {modalBorrar && (
        <ModalBorrarArchivados
          registros={modalBorrar}
          onCerrar={() => setModalBorrar(null)}
          onTerminado={() => {
            setModalBorrar(null);
            setModoSeleccion(false);
            setArchivadosSeleccionados(new Set());
            recargar();
          }}
        />
      )}
    </div>
  );
}

// ============================================
// FILTROS — chips de estado / dropdowns / resumen
// ============================================

function ChipsEstado({ valor, onCambio }) {
  const ESTADOS_FILTRABLES = ['basico', 'operativo', 'listo_facturar'];
  const todosActivo = valor == null;
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      <button
        onClick={() => onCambio(null)}
        title="Mostrar clientes en cualquier estado"
        style={{
          padding: '6px 12px', borderRadius: 99,
          border: `1px solid ${todosActivo ? COLOR_OSCURO : COLOR_BORDE}`,
          background: todosActivo ? COLOR_OSCURO : '#fff',
          color: todosActivo ? '#fff' : COLOR_GRIS,
          fontSize: 12, fontWeight: 600,
          cursor: 'pointer', fontFamily: PP,
        }}
      >Todos los estados</button>
      {ESTADOS_FILTRABLES.map(estado => {
        const meta = ESTADO_VISUAL[estado];
        const activo = valor === estado;
        return (
          <button
            key={estado}
            onClick={() => onCambio(activo ? null : estado)}
            title={meta.descripcion}
            style={{
              padding: '6px 12px', borderRadius: 99,
              border: `1px solid ${meta.color}`,
              background: activo ? meta.color : '#fff',
              color: activo ? '#fff' : meta.color,
              fontSize: 12, fontWeight: 600,
              cursor: 'pointer', fontFamily: PP,
              transition: 'background 0.15s, color 0.15s',
            }}
          >{meta.label}</button>
        );
      })}
    </div>
  );
}

function DropdownFiltro({ label, todosLabel, valor, opciones, onCambio }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: COLOR_GRIS }}>
      <span style={{ fontWeight: 600 }}>{label}:</span>
      <select
        value={valor == null ? '' : String(valor)}
        onChange={(e) => {
          const v = e.target.value;
          if (v === '') onCambio(null);
          else {
            // Mantener tipo numérico cuando el value original fue número
            const opt = opciones.find(o => String(o.value) === v);
            onCambio(opt ? opt.value : v);
          }
        }}
        style={{
          padding: '6px 10px', border: `1px solid ${COLOR_BORDE}`, borderRadius: 8,
          fontSize: 12, fontFamily: PP, background: '#fff', color: COLOR_OSCURO,
          cursor: 'pointer', minWidth: 160,
        }}
      >
        <option value="">{todosLabel}</option>
        {opciones.map(o => (
          <option key={String(o.value)} value={String(o.value)}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

function ResumenFiltros({
  estadoFiltro, grupoFiltro, ciudadFiltro,
  grupoOpciones, onQuitarEstado, onQuitarGrupo, onQuitarCiudad, onLimpiar,
}) {
  const grupoNombre = grupoFiltro != null
    ? (grupoOpciones.find(g => g.id === grupoFiltro)?.nombre || `Grupo #${grupoFiltro}`)
    : null;
  const estadoMeta = estadoFiltro ? ESTADO_VISUAL[estadoFiltro] : null;
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', fontSize: 12 }}>
      <span style={{ color: COLOR_GRIS_CLARO, fontWeight: 600 }}>Filtros activos:</span>
      {estadoMeta && (
        <ChipActivo
          label={estadoMeta.label}
          color={estadoMeta.color}
          onQuitar={onQuitarEstado}
        />
      )}
      {grupoNombre && (
        <ChipActivo
          label={grupoNombre}
          color={COLOR_PRINCIPAL}
          onQuitar={onQuitarGrupo}
        />
      )}
      {ciudadFiltro && (
        <ChipActivo
          label={ciudadFiltro}
          color={COLOR_PRINCIPAL}
          onQuitar={onQuitarCiudad}
        />
      )}
      <button
        onClick={onLimpiar}
        style={{
          background: 'transparent', border: 'none',
          color: COLOR_PRINCIPAL, fontSize: 12, fontWeight: 600,
          cursor: 'pointer', fontFamily: PP, padding: '4px 6px', marginLeft: 4,
        }}
      >Limpiar todo</button>
    </div>
  );
}

function ChipActivo({ label, color, onQuitar }) {
  return (
    <span
      onClick={onQuitar}
      title="Quitar filtro"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: color + '14', color, border: `1px solid ${color}55`,
        padding: '3px 8px 3px 10px', borderRadius: 99,
        fontSize: 12, fontWeight: 600, fontFamily: PP, cursor: 'pointer',
      }}
    >
      {label}
      <span style={{ fontSize: 14, lineHeight: 1, opacity: 0.7 }}>×</span>
    </span>
  );
}

// ============================================
// ÍTEM DE LISTA
// ============================================

function ItemLista({ item, onClick, onArchivado, mostrarArchivados, seleccionable, seleccionado, ocultarAcciones }) {
  // Única fuente: ESTADO_VISUAL. Si el estado no está en el catálogo,
  // fallback a 'basico' (defensivo).
  const visual = ESTADO_VISUAL[item.estado] || ESTADO_VISUAL.basico;
  // En la pestaña Archivados, neutralizamos el bg y el badge (todos
  // están archivados — el contexto ya lo dice).
  const bgBase = (!mostrarArchivados && visual.bg && visual.bg !== 'transparent')
    ? visual.bg
    : '#fff';
  const mostrarBadge = !mostrarArchivados && !!visual.label;
  // Tooltip combina label + descripción, separados por salto de línea
  // (el navegador lo respeta en title="").
  const tooltipEstado = `${visual.label}\n${visual.descripcion}`;
  return (
    <div
      onClick={onClick}
      title={tooltipEstado}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px 14px', background: bgBase,
        border: `1px solid ${COLOR_BORDE}`, borderRadius: 10,
        cursor: 'pointer', transition: 'all 0.15s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = COLOR_PRINCIPAL; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = COLOR_BORDE; }}
    >
      {seleccionable && (
        /* SPRINT SUCURSALES 1: checkbox del modo seleccion (area tactil 44px,
           con feedback al toque — hallazgo de Don Porfirio). */
        <div
          aria-label={seleccionado ? 'Quitar de la seleccion' : 'Agregar a la seleccion'}
          onPointerDown={(e) => { e.currentTarget.style.opacity = '0.5'; }}
          onPointerUp={(e) => { e.currentTarget.style.opacity = '1'; }}
          onPointerLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
          style={{
            width: 44, height: 44, marginLeft: -8, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'opacity 0.1s',
          }}
        >
          <div style={{
            width: 22, height: 22, borderRadius: 6,
            border: `2px solid ${seleccionado ? COLOR_PRINCIPAL : COLOR_BORDE}`,
            background: seleccionado ? COLOR_PRINCIPAL : '#fff',
            color: '#fff', fontSize: 14, fontWeight: 800,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>{seleccionado ? '✓' : ''}</div>
        </div>
      )}
      <div style={{ fontSize: 22 }}>{item.icono}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: COLOR_OSCURO }}>{item.nombre}</div>
        {item.resumen && (
          <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.resumen}
          </div>
        )}
      </div>
      {mostrarBadge && (
        <span
          title={tooltipEstado}
          style={{
            background: visual.color + '22', color: visual.color,
            padding: '3px 8px', borderRadius: 99, fontSize: 10, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: 0.4, flexShrink: 0,
            whiteSpace: 'nowrap',
          }}
        >{visual.label}</span>
      )}
      <div
        title={tooltipEstado}
        style={{
          width: 10, height: 10, borderRadius: '50%',
          background: visual.color, flexShrink: 0,
        }}
      />
      {/* BORRADO MASIVO: en modo seleccion se ocultan las acciones individuales
          (evita la doble affordance: la fila entera es el toggle de seleccion). */}
      {!ocultarAcciones && (mostrarArchivados ? (
        <AccionesArchivado
          tipo={item.tipo}
          id={item.id}
          entidad={item}
          onCambio={onArchivado}
        />
      ) : (
        <BotonArchivarCliente
          variante="compacto"
          tipo={item.tipo}
          id={item.id}
          entidad={item}
          onArchivado={onArchivado}
        />
      ))}
    </div>
  );
}

// ============================================
// MODAL DE CREACIÓN RÁPIDA
// ============================================

function ModalCrear({ onCerrar, onCreado, razones, grupos }) {
  const [paso, setPaso] = useState('elegir'); // 'elegir' | tipo específico
  const [form, setForm] = useState({ padreTipo: 'razon_social' }); // sucursal: 'razon_social' | 'grupo'
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [similaresCount, setSimilaresCount] = useState(0);
  // Candado anti-duplicados del backend: si el POST responde 409 (duplicado exacto que el
  // detector en vivo pudo no marcar, p.ej. "Av"/"Avenida"), guardamos aqui el aviso para pedir
  // confirmacion antes de crear.
  const [dupBackend, setDupBackend] = useState(null);

  const actualizar = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Nombre actual para detección de duplicados según el paso
  const nombrePaso = {
    grupo: form.nombre,
    razon_social: form.razon_social,
    sucursal: form.nombre_interno,
    persona_fisica: form.nombre_completo,
  }[paso] || '';

  // contextoId: para sucursal, mandamos el id del padre seleccionado
  const contextoId = paso === 'sucursal'
    ? (form.padreTipo === 'razon_social' ? form.razon_social_id : form.grupo_id) || null
    : null;

  // Tipo que espera el endpoint buscar-similares
  const tipoDetector = {
    grupo: 'grupo', razon_social: 'razon_social', sucursal: 'sucursal', persona_fisica: 'persona',
  }[paso];

  const guardar = async (forzar = false) => {
    setGuardando(true); setError(null);
    try {
      let url, body, tipo;
      if (paso === 'grupo') {
        url = '/api/clientes/grupos';
        body = { nombre: form.nombre };
        tipo = 'grupo';
      } else if (paso === 'razon_social') {
        url = '/api/clientes/razones-sociales';
        body = { razon_social: form.razon_social, grupo_id: form.grupo_id || null };
        tipo = 'razon_social';
      } else if (paso === 'sucursal') {
        url = '/api/clientes/sucursales';
        body = {
          nombre_interno: form.nombre_interno,
          razon_social_id: form.padreTipo === 'razon_social' ? form.razon_social_id : null,
          grupo_id: form.padreTipo === 'grupo' ? form.grupo_id : null,
        };
        tipo = 'sucursal';
      } else if (paso === 'persona_fisica') {
        url = '/api/clientes/personas';
        body = { nombre_completo: form.nombre_completo, es_cliente_persona_fisica: true };
        tipo = 'persona';
      }
      if (forzar) body.forzar_crear = true;
      const r = await fetch(`${API_BASE}${url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      });
      const j = await r.json();
      // Candado anti-duplicados del backend (409): mostrar aviso y dejar decidir, no crear a ciegas.
      if (r.status === 409 && j && j.duplicado) {
        setDupBackend({ mensaje: j.mensaje, similares: j.similares || [], tipo });
        return;
      }
      if (!j.ok) throw new Error(j.error || 'Error');
      const creado = j.grupo || j.razon_social || j.sucursal || j.persona;
      onCreado(creado ? { tipo, id: creado.id } : null);
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  };

  // Callback cuando el usuario elige un existente en el detector
  const usarExistente = (item) => onCreado({ tipo: item.tipo, id: item.id });

  const overlay = {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(26,58,92,0.45)', zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
  };
  const card = {
    background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 460,
    fontFamily: PP, color: COLOR_GRIS,
    // Red de seguridad: si el contenido crece (varios duplicados) el modal hace scroll interno
    // en vez de salirse de la pantalla.
    maxHeight: 'calc(var(--beep-vh) * 90)', overflowY: 'auto',
  };
  const input = {
    width: '100%', padding: '10px 12px', border: `1px solid ${COLOR_BORDE}`,
    borderRadius: 8, fontSize: 13, fontFamily: PP, outline: 'none', marginTop: 6,
  };
  const select = { ...input, background: '#fff' };

  return (
    <div style={overlay} onClick={onCerrar}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 16 }}>
          {paso === 'elegir' ? '¿Qué tipo de cliente quieres crear?' : 'Nuevo cliente'}
        </div>

        {paso === 'elegir' && (
          <div style={{ display: 'grid', gap: 10 }}>
            <Opcion icono="🏢" titulo="Grupo (cliente)" sub="Paraguas comercial de varias razones sociales" onClick={() => setPaso('grupo')} />
            <Opcion icono="🏢" titulo="Razón Social" sub="Razón social a la que facturamos (opcionalmente en un grupo (cliente))" onClick={() => setPaso('razon_social')} />
            <Opcion icono="🏪" titulo="Sucursal" sub="Centro de operaciones dentro de una razón social" onClick={() => setPaso('sucursal')} />
            <Opcion icono="👤" titulo="Persona Física" sub="Cliente persona física directa" onClick={() => setPaso('persona_fisica')} />
          </div>
        )}

        {paso === 'grupo' && (
          <div>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Nombre del grupo (cliente) *</label>
            <input autoFocus style={input} value={form.nombre || ''} onChange={e => actualizar('nombre', e.target.value)} placeholder="Ej: Grupo Alianza" />
            <DetectorDuplicados
              tipo="grupo"
              nombreActual={form.nombre || ''}
              onSeleccionarExistente={usarExistente}
              onCambioResultados={setSimilaresCount}
            />
          </div>
        )}

        {paso === 'razon_social' && (
          <div>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Razón social *</label>
            <input autoFocus style={input} value={form.razon_social || ''} onChange={e => actualizar('razon_social', e.target.value)} placeholder="Ej: Comercializadora del Sur S.A. de C.V." />
            <DetectorDuplicados
              tipo="razon_social"
              nombreActual={form.razon_social || ''}
              onSeleccionarExistente={usarExistente}
              onCambioResultados={setSimilaresCount}
            />
            <label style={{ fontSize: 12, fontWeight: 600, marginTop: 12, display: 'block' }}>Grupo (cliente) (opcional)</label>
            <select style={select} value={form.grupo_id || ''} onChange={e => actualizar('grupo_id', e.target.value || null)}>
              <option value="">— Sin grupo (cliente) —</option>
              {grupos.map(g => <option key={g.id} value={g.id}>{g.nombre}</option>)}
            </select>
          </div>
        )}

        {paso === 'sucursal' && (
          <div>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Asignar a *</label>
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                <input
                  type="radio" name="padreTipo" value="razon_social"
                  checked={form.padreTipo === 'razon_social'}
                  onChange={() => actualizar('padreTipo', 'razon_social')}
                />
                Razón social
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                <input
                  type="radio" name="padreTipo" value="grupo"
                  checked={form.padreTipo === 'grupo'}
                  onChange={() => actualizar('padreTipo', 'grupo')}
                />
                Grupo (cliente) (temporal)
              </label>
            </div>

            {form.padreTipo === 'razon_social' ? (
              <>
                <label style={{ fontSize: 12, fontWeight: 600, marginTop: 12, display: 'block' }}>Razón social padre *</label>
                <select style={select} value={form.razon_social_id || ''} onChange={e => actualizar('razon_social_id', e.target.value)}>
                  <option value="">— Selecciona —</option>
                  {razones.map(rs => <option key={rs.id} value={rs.id}>{rs.razon_social}</option>)}
                </select>
              </>
            ) : (
              <>
                <label style={{ fontSize: 12, fontWeight: 600, marginTop: 12, display: 'block' }}>Grupo (cliente) padre *</label>
                <select style={select} value={form.grupo_id || ''} onChange={e => actualizar('grupo_id', e.target.value)}>
                  <option value="">— Selecciona —</option>
                  {grupos.map(g => <option key={g.id} value={g.id}>{g.nombre}</option>)}
                </select>
                <div style={{
                  marginTop: 8, padding: 10, background: '#fff4e5', borderRadius: 6,
                  fontSize: 11, color: '#a86b1e', lineHeight: 1.4,
                }}>
                  ⚠️ Esta sucursal quedará pendiente de asignar razón social para poder facturar.
                </div>
              </>
            )}

            <label style={{ fontSize: 12, fontWeight: 600, marginTop: 12, display: 'block' }}>Nombre interno *</label>
            <input style={input} value={form.nombre_interno || ''} onChange={e => actualizar('nombre_interno', e.target.value)} placeholder="Ej: Tlalpan" />
            <DetectorDuplicados
              tipo="sucursal"
              nombreActual={form.nombre_interno || ''}
              contextoId={contextoId}
              onSeleccionarExistente={usarExistente}
              onCambioResultados={setSimilaresCount}
            />
          </div>
        )}

        {paso === 'persona_fisica' && (
          <div>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Nombre completo *</label>
            <input autoFocus style={input} value={form.nombre_completo || ''} onChange={e => actualizar('nombre_completo', e.target.value)} placeholder="Ej: María López Hernández" />
            <DetectorDuplicados
              tipo="persona"
              nombreActual={form.nombre_completo || ''}
              onSeleccionarExistente={usarExistente}
              onCambioResultados={setSimilaresCount}
            />
          </div>
        )}

        {error && <div style={{ color: '#e05c5c', fontSize: 12, marginTop: 10 }}>{error}</div>}

        {/* CANDADO ANTI-DUPLICADOS (aviso del backend 409): el usuario decide. */}
        {dupBackend && (
          <div style={{ marginTop: 12, padding: 12, border: '1px solid #f0c36d', background: '#fff4e5', borderRadius: 10 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 8 }}>{dupBackend.mensaje}</div>
            <div style={{ display: 'grid', gap: 6, marginBottom: 10 }}>
              {dupBackend.similares.map((s) => (
                <button key={s.id} onClick={() => usarExistente({ tipo: dupBackend.tipo, id: s.id })}
                  style={{ textAlign: 'left', padding: '10px 12px', border: `1px solid ${COLOR_BORDE}`, background: '#fff', borderRadius: 8, fontSize: 14, cursor: 'pointer', fontFamily: PP, minHeight: 44 }}>
                  Usar el existente: <b>{s.nombre}</b>
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                onClick={() => { setDupBackend(null); guardar(true); }}
                disabled={guardando}
                style={{ flex: 1, minWidth: 160, minHeight: 44, padding: '8px 12px', border: 'none', background: guardando ? '#c9d6e3' : COLOR_PRINCIPAL, color: '#fff', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: guardando ? 'not-allowed' : 'pointer', fontFamily: PP }}>
                {guardando ? 'Creando…' : 'Crear de todos modos'}
              </button>
              <button onClick={() => setDupBackend(null)} disabled={guardando}
                style={{ minHeight: 44, padding: '8px 14px', border: `1px solid ${COLOR_BORDE}`, background: '#fff', color: COLOR_GRIS, borderRadius: 8, fontSize: 14, cursor: guardando ? 'not-allowed' : 'pointer', fontFamily: PP }}>
                Cancelar
              </button>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button onClick={paso === 'elegir' ? onCerrar : () => { setPaso('elegir'); setForm({ padreTipo: 'razon_social' }); setError(null); setSimilaresCount(0); }}
            style={{ padding: '8px 16px', border: `1px solid ${COLOR_BORDE}`, background: '#fff', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontFamily: PP }}>
            {paso === 'elegir' ? 'Cancelar' : 'Atrás'}
          </button>
          {paso !== 'elegir' && !dupBackend && (
            <button
              // SIEMPRE guardar(false): dejamos que el candado ESTRICTO del backend (409 por nombre
              // normalizado equivalente) decida. Nunca forzamos desde aqui aunque el detector vivo
              // (fuzzy) haya mostrado parecidos -> forzar solo desde "Crear de todos modos".
              onClick={() => guardar(false)}
              disabled={guardando || !puedeGuardar(paso, form)}
              style={{
                padding: '8px 18px', border: 'none',
                background: puedeGuardar(paso, form) && !guardando ? COLOR_PRINCIPAL : '#c9d6e3',
                color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 600,
                cursor: puedeGuardar(paso, form) && !guardando ? 'pointer' : 'not-allowed',
                fontFamily: PP,
              }}
            >
              {guardando
                ? 'Guardando…'
                : similaresCount > 0
                  ? `Sí, crear ${etiquetaTipo(paso)}`
                  : 'Crear'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function puedeGuardar(paso, form) {
  if (paso === 'grupo') return !!(form.nombre && form.nombre.trim());
  if (paso === 'razon_social') return !!(form.razon_social && form.razon_social.trim());
  if (paso === 'sucursal') {
    const nombreOk = !!(form.nombre_interno && form.nombre_interno.trim());
    if (!nombreOk) return false;
    return form.padreTipo === 'razon_social' ? !!form.razon_social_id : !!form.grupo_id;
  }
  if (paso === 'persona_fisica') return !!(form.nombre_completo && form.nombre_completo.trim());
  return false;
}

function etiquetaTipo(paso) {
  return {
    grupo: 'nuevo grupo',
    razon_social: 'nueva razón social',
    sucursal: 'nueva sucursal',
    persona_fisica: 'nueva persona',
  }[paso] || 'nuevo';
}

function etiquetaRol(tipo) {
  return {
    administrador: 'Administrador',
    gerente: 'Gerente',
    encargado_sucursal: 'Encargado',
    propietario: 'Propietario',
    contacto: 'Contacto',
  }[tipo] || tipo;
}

// ============================================
// VISUAL POR ESTADO — única fuente de verdad
// Usa el campo `estado` de la BD (mismo que el sistema decide vía
// helpers en routes/clientes.js). Dot, badge y bg se alinean al mismo
// color para no contradecirse. `descripcion` se usa como tooltip al hover.
// ============================================
const ESTADO_VISUAL = {
  basico: {
    bg: 'transparent',
    color: '#dc2626',
    label: 'Básico',
    descripcion: 'Cliente recién creado. Llena los datos para activarlo.',
  },
  operativo: {
    bg: '#f0fdf4',
    color: '#10b981',
    label: 'Operativo',
    descripcion: 'Cliente con información funcional. Listo para trabajar.',
  },
  listo_facturar: {
    bg: '#dbeafe',
    color: '#185FA5',
    label: 'Listo para facturar',
    descripcion: 'Datos fiscales completos. Puede emitirse factura.',
  },
  archivado: {
    bg: 'transparent',
    color: '#9ca3af',
    label: 'Archivado',
    descripcion: 'Fuera de circulación. Puede restaurarse o borrarse.',
  },
};

function Opcion({ icono, titulo, sub, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: 12,
        border: `1px solid ${COLOR_BORDE}`, borderRadius: 10, cursor: 'pointer',
        transition: 'border 0.15s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = COLOR_PRINCIPAL; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = COLOR_BORDE; }}
    >
      <div style={{ fontSize: 24 }}>{icono}</div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: COLOR_OSCURO }}>{titulo}</div>
        <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 2 }}>{sub}</div>
      </div>
    </div>
  );
}
